import { useMemo } from 'react'

import { useChannel } from '@core/channel'
import { FeedQueryWrapper } from '@core/feed/FeedQueryWrapper'
import { useMapParamsToConfiguration } from '@core/widgets/embedFeed'
import { useStore } from '@core/widgets/store'
import { paramsSelector } from '@core/widgets/store/selectors'
import type { AppContextType } from '@embed/components/helpers'
import { useCohorts } from '@embed/hooks/useCohorts'

interface Props {
  appContextType: AppContextType
}

/**
 * HOC to resolve embed feed config and render wrapper with feed context
 */
export const WidgetFeed: React.FC<React.PropsWithChildren<Props>> = ({
  appContextType,
  children,
}) => {
  const params = useStore(paramsSelector)
  const cohortParams = useStore((state) => state.fromCohortParams)

  const channel = useChannel({
    username: params.channel,
    encodedId: params.channel_id,
    target: params.target,
    widgetConfigId: params.widget_config_id,
  })

  const mergedParams = useMemo(
    () => ({ ...params, ...cohortParams }),
    [cohortParams, params],
  )

  const configs = useMapParamsToConfiguration({
    appContextType,
    params: mergedParams,
    channel,
  })

  useCohorts({ channel })

  if (!channel || !configs?.length) {
    return null
  }

  return (
    <FeedQueryWrapper configs={configs} params={params}>
      {children}
    </FeedQueryWrapper>
  )
}
